// Your backyard, drawn in game pixels over the garden map: what's growing in each soil plot (a seed in the dirt,
// a sprout, leafy tops, then the crop itself showing), a sparkle when something's ready, and your animals
// pottering about their pens (hens in the run, the cow and the sheep in the paddock).
import ink from '../ink/ink.js';
import { ANIMALS, COOP_RUN, FED_MS, GARDEN_RANGE_PX, PADDOCK, PEN_RANGE_PX, PLOTS, animalReady, cropDef, growth, type AnimalView, type GardenView, type PlotView } from '../../../shared/garden.js';

const I = ink as any;
const { INK, TAU } = I;
const TS = 16;

/** The soil plots: wet soil after watering, and the crop at its stage. Drawn on the ground, under everyone. */
export function drawGardenGround(g: CanvasRenderingContext2D, v: GardenView, now: number, skew: number): void {
  for (const p of v.plots) drawPlot(g, p, now + skew);
}
function drawPlot(g: CanvasRenderingContext2D, p: PlotView, now: number): void {
  const [tx, ty] = PLOTS[p.plot], x = tx * TS + 8, y = ty * TS + 8;
  g.save(); g.lineJoin = 'round'; g.lineCap = 'round';
  // tilled rows (darker when watered this stage)
  g.fillStyle = p.watered ? 'rgba(92,62,40,0.55)' : 'rgba(122,84,52,0.35)';
  g.beginPath(); g.ellipse(x, y + 1, 7, 4.5, 0, 0, TAU); g.fill();
  g.strokeStyle = 'rgba(62,47,40,0.35)'; g.lineWidth = 0.6;
  for (const dy of [-2, 0.5, 3]) { g.beginPath(); g.moveTo(x - 5, y + dy); g.lineTo(x + 5, y + dy); g.stroke(); }
  const c = cropDef(p.crop);
  if (!c) { g.restore(); return; }
  const gr = growth(p, now), sway = Math.sin(now / 700 + p.plot) * 0.6;
  const leaf = (lx: number, ly: number, len: number, ang: number, col = '#6aa04a') => {
    g.save(); g.translate(lx, ly); g.rotate(ang + sway * 0.08);
    g.fillStyle = col; g.strokeStyle = INK; g.lineWidth = 0.5;
    g.beginPath(); g.moveTo(0, 0); g.quadraticCurveTo(len * 0.4, -len * 0.5, 0, -len); g.quadraticCurveTo(-len * 0.4, -len * 0.5, 0, 0); g.fill(); g.stroke(); g.restore();
  };
  if (gr.stage === 'sprout') { leaf(x - 1, y + 1, 3.5, -0.5, '#9fd36a'); leaf(x + 1, y + 1, 3.5, 0.5, '#9fd36a'); }
  else if (gr.stage === 'leafy') { for (const [dx, a] of [[-2.5, -0.6], [0, 0], [2.5, 0.6], [-1, -0.25], [1, 0.25]] as [number, number][]) leaf(x + dx, y + 1, 4.5 + gr.k * 3, a); }
  else {
    // ripe: the crop shows itself
    if (c.id === 'pumpkin') {
      leaf(x - 4, y + 1, 5, -1.1); leaf(x + 4, y + 1, 5, 1.1);
      g.fillStyle = c.color; g.strokeStyle = INK; g.lineWidth = 0.7; g.beginPath(); g.ellipse(x, y - 1, 5.5, 4, 0, 0, TAU); g.fill(); g.stroke();
      g.strokeStyle = 'rgba(62,47,40,0.45)'; g.lineWidth = 0.5; for (const dx of [-2, 0, 2]) { g.beginPath(); g.moveTo(x + dx, y - 4.5); g.quadraticCurveTo(x + dx * 1.4, y - 1, x + dx, y + 2.5); g.stroke(); }
      g.fillStyle = '#6aa04a'; g.fillRect(x - 0.6, y - 6.5, 1.2, 2);
    } else if (c.id === 'strawberry') {
      for (const [dx, a] of [[-3, -0.7], [0, 0], [3, 0.7]] as [number, number][]) leaf(x + dx, y + 1, 5.5, a);
      for (const [dx, dy] of [[-3, -1], [2.5, -2], [0, 1]] as [number, number][]) { g.fillStyle = c.color; g.strokeStyle = INK; g.lineWidth = 0.5; g.beginPath(); g.ellipse(x + dx, y + dy, 1.6, 2, 0, 0, TAU); g.fill(); g.stroke(); g.fillStyle = '#f7d35e'; g.fillRect(x + dx - 0.3, y + dy - 0.3, 0.6, 0.6); }
    } else {
      // carrots and turnips: leafy tops over a shoulder poking out of the soil
      for (const [dx, a] of [[-2, -0.5], [0, 0], [2, 0.5]] as [number, number][]) leaf(x + dx, y - 1, 6, a, c.id === 'turnip' ? '#7fae5a' : '#6aa04a');
      g.fillStyle = c.id === 'turnip' ? '#9a6ac2' : c.color; g.strokeStyle = INK; g.lineWidth = 0.6;
      g.beginPath(); g.ellipse(x, y, 3.2, 2.2, 0, Math.PI, 0); g.closePath(); g.fill(); g.stroke();
    }
    // ready: a little sparkle
    const k = (now / 900 + p.plot) % 1; I.star4(g, x + 5, y - 7 - k * 2, 1.6 * Math.sin(k * Math.PI), 0, '#f7d35e', 0.4);
  }
  g.restore();
}

/** Your animals, to y-sort with everyone else: each wanders its pen on a gentle loop of its own. */
export function gardenItems(g: CanvasRenderingContext2D, v: GardenView, now: number, skew: number): { y: number; draw: () => void }[] {
  const byPen = { coop: v.animals.filter(a => ANIMALS[a.kind].pen === 'coop'), paddock: v.animals.filter(a => ANIMALS[a.kind].pen === 'paddock') };
  const out: { y: number; draw: () => void }[] = [];
  for (const pen of ['coop', 'paddock'] as const) byPen[pen].forEach((a, i) => {
    const r = pen === 'coop' ? COOP_RUN : PADDOCK, w = (r[2] - r[0] + 1) * TS - 10, h = (r[3] - r[1] + 1) * TS - 8;
    const t = now / 1000 * (a.kind === 'hen' ? 0.22 : 0.08) + a.id * 1.7 + i;
    const x = r[0] * TS + 5 + (Math.sin(t) * 0.5 + 0.5) * w, y = r[1] * TS + 8 + (Math.sin(t * 1.37 + 2) * 0.5 + 0.5) * h;
    const dir = Math.cos(t) >= 0 ? 1 : -1, ready = animalReady(a, now + skew), hungry = now + skew - a.fedAt > FED_MS;
    out.push({ y, draw: () => drawAnimal(g, a, x, y, dir, now, ready, hungry) });
  });
  return out;
}
function drawAnimal(g: CanvasRenderingContext2D, a: AnimalView, x: number, y: number, dir: number, now: number, ready: boolean, hungry: boolean): void {
  const t = now / 1000 + a.id, bob = Math.abs(Math.sin(t * (a.kind === 'hen' ? 9 : 4))) * (a.kind === 'hen' ? 0.8 : 0.4);
  g.save(); g.translate(Math.round(x * 3) / 3, Math.round(y * 3) / 3);
  g.fillStyle = 'rgba(62,47,40,0.2)'; g.beginPath(); g.ellipse(0, 0.5, a.kind === 'hen' ? 3.5 : 7, 1.6, 0, 0, TAU); g.fill();
  g.scale(dir, 1); g.translate(0, -bob);
  g.lineWidth = 0.6; g.strokeStyle = INK; g.lineJoin = 'round';
  const blob = (bx: number, by: number, rx: number, ry: number, col: string) => { g.fillStyle = col; g.beginPath(); g.ellipse(bx, by, rx, ry, 0, 0, TAU); g.fill(); g.stroke(); };
  const eye = (ex: number, ey: number) => { g.fillStyle = INK; g.beginPath(); g.arc(ex, ey, 0.55, 0, TAU); g.fill(); };
  if (a.kind === 'hen') {
    g.beginPath(); g.moveTo(-1, 0); g.lineTo(-1, -1.6); g.moveTo(1, 0); g.lineTo(1, -1.6); g.strokeStyle = '#d4892e'; g.stroke(); g.strokeStyle = INK;
    blob(0, -3.5, 3.6, 2.8, '#fbf4e6');
    g.fillStyle = '#fbf4e6'; g.beginPath(); g.moveTo(-3, -4.5); g.lineTo(-5, -6.5); g.lineTo(-3.6, -3.2); g.fill(); g.stroke();   // tail
    blob(2.6, -6.4, 1.9, 1.9, '#fbf4e6');
    g.fillStyle = '#e3604b'; g.beginPath(); g.arc(2.6, -8.4, 0.9, 0, TAU); g.fill();                                       // comb
    g.fillStyle = '#f2c14e'; g.beginPath(); g.moveTo(4.3, -6.6); g.lineTo(5.6, -6.1); g.lineTo(4.3, -5.6); g.fill();       // beak
    eye(3.2, -6.7);
  } else if (a.kind === 'cow') {
    for (const lx of [-4, -1.5, 2, 4.5]) { g.fillStyle = '#f6f2ea'; g.fillRect(lx - 0.7, -3, 1.4, 3); g.strokeRect(lx - 0.7, -3, 1.4, 3); }
    blob(0, -5.5, 6.5, 3.6, '#f6f2ea');
    g.fillStyle = INK; for (const [sx, sy, r] of [[-2.5, -6.5, 1.4], [1.5, -4.5, 1.1], [3.5, -7, 0.9]] as [number, number, number][]) { g.beginPath(); g.arc(sx, sy, r, 0, TAU); g.fill(); }
    blob(6.6, -7.2, 2.6, 2.2, '#f6f2ea'); blob(8, -6.4, 1.4, 1, '#f4c4c4');
    g.fillStyle = '#e8d9c0'; g.beginPath(); g.moveTo(5.5, -9); g.lineTo(5, -10.6); g.lineTo(6.3, -9.3); g.fill(); g.stroke();
    eye(6.8, -7.8);
  } else {
    for (const lx of [-3.5, -1, 2, 4]) { g.fillStyle = '#5a5560'; g.fillRect(lx - 0.6, -2.5, 1.2, 2.5); }
    for (const [cx, cy, r] of [[-3, -5, 2.6], [0, -6, 2.9], [3, -5.2, 2.6], [-1.5, -3.8, 2.4], [1.8, -3.8, 2.4]] as [number, number, number][]) blob(cx, cy, r, r * 0.9, '#f6f2ea');
    blob(5.5, -6, 2, 1.8, '#5a5560'); g.fillStyle = '#fbf7ee'; g.beginPath(); g.arc(6.2, -6.4, 0.5, 0, TAU); g.fill();
  }
  g.restore();
  // what it needs: a heart when there's something to collect, a little bowl when it's hungry
  if (ready || hungry) { const k = (now / 1200 + a.id) % 1, top = a.kind === 'hen' ? 12 : 14; g.save(); g.globalAlpha = 0.6 + Math.sin(k * Math.PI) * 0.4; g.font = '7px system-ui'; g.textAlign = 'center'; g.fillText(ready ? '❤' : '🥣', x, y - top - k * 1.5); g.restore(); }
}

/** What pressing E does here: the plot or the animal you're next to. */
export function gardenTarget(v: GardenView, px: number, py: number): { plot: number } | { animal: AnimalView } | null {
  let best: { plot: number } | null = null, bd = GARDEN_RANGE_PX + 2;
  PLOTS.forEach(([tx, ty], i) => { const d = Math.hypot(px - (tx * TS + 8), py - (ty * TS + 8)); if (d < bd) { bd = d; best = { plot: i }; } });
  if (best) return best;
  for (const pen of ['coop', 'paddock'] as const) {
    const r = pen === 'coop' ? COOP_RUN : PADDOCK, nx = Math.max(r[0] * TS, Math.min((r[2] + 1) * TS, px)), ny = Math.max(r[1] * TS, Math.min((r[3] + 1) * TS, py));
    if (Math.hypot(px - nx, py - ny) > PEN_RANGE_PX - 4) continue;
    const here = v.animals.filter(a => ANIMALS[a.kind].pen === pen);
    const pick = here.find(a => animalReady(a, Date.now())) ?? here.find(a => Date.now() - a.fedAt > FED_MS) ?? here[0];
    if (pick) return { animal: pick };
  }
  return null;
}
